import {
  CheckOutlined,
  CloseOutlined,
  DownOutlined,
  InfoCircleFilled,
  UpOutlined
} from '@ant-design/icons'
import type { UserRole } from '@lmc/ts-common'
import { Icon, IconType } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { reportingConstants } from 'locale/reporting'
import { reportsConstants } from 'locale/team'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import ExternalLinkIcon from './ExternalLinkIcon'
import { ROLE_PREVIEW_COPY } from './roleInfoCopy'

export type RoleInfoCardProps = {
  role: UserRole | string
  collapsible?: boolean
  defaultExpanded?: boolean
  testId?: string
}

// Roles & Permissions overview support article (URL wired in CM-3387).
// Kept nullable so the inert-link fallback below still type-checks.
const OVERVIEW_LINK_URL: string | null =
  'https://support.logmycare.co.uk/en/articles/15821229-roles-and-permissions'

const Card = styled.div<{ $expanded: boolean; $bare: boolean }>`
  ${({ $bare }) =>
    $bare
      ? ''
      : `
    background: #f0fcff;
    border: 1px solid #8fcafd;
    border-radius: 8px;
    padding: 16px;
  `}
  display: flex;
  flex-direction: column;
  gap: ${({ $expanded }) => ($expanded ? '8px' : '0')};
  width: 100%;
`

const Heading = styled.div<{ $clickable: boolean; $linkStyle: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 24px;
  letter-spacing: 0.4px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  font-weight: ${({ $linkStyle }) => ($linkStyle ? 500 : 700)};
  font-size: ${({ $linkStyle }) => ($linkStyle ? '16px' : '18px')};
  color: ${({ $linkStyle, theme }) =>
    $linkStyle ? theme.color.status.primary : theme.color.neutral.color3};
`

// Only the label is underlined (link affordance) — the info icon and chevron
// sit outside the underline.
const HeadingLabel = styled.span<{ $underline: boolean }>`
  text-decoration: ${({ $underline }) => ($underline ? 'underline' : 'none')};
`

const Chevron = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  color: ${({ theme }) => theme.color.status.primary};
`

// Side-panel (collapsible) expanded content sits behind a grey rule and is
// indented (CM-3787). The card/modal variant keeps its content flush.
const ExpandedBody = styled.div<{ $bordered: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  ${({ $bordered, theme }) =>
    $bordered
      ? `
    margin-left: 1px;
    padding-left: 16px;
    border-left: 4px solid ${theme.color.neutral.color8};
  `
      : ''}
`

const SectionTitle = styled.p`
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.color.neutral.color3};
  margin: 4px 0 0;
`

const SectionItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`

const Item = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  color: ${({ theme }) => theme.color.neutral.color3};
  font-size: 16px;
  line-height: 24px;
`

const TickGlyph = styled(CheckOutlined)`
  color: ${({ theme }) => theme.color.status.success};
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 4px;
`

const CrossGlyph = styled(CloseOutlined)`
  color: ${({ theme }) => theme.color.status.danger};
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 4px;
`

const InlineSettingsIcon = styled(Icon)`
  display: inline-flex;
  vertical-align: middle;
  margin: 0 2px;
`

const ExtraNote = styled.p`
  margin: 8px 0 0;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const KeyworkerLink = styled.a`
  color: ${({ theme }) => theme.color.status.primary};
  text-decoration: underline;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`

const OverviewLink = styled.a`
  margin-top: 8px;
  color: ${({ theme }) => theme.color.status.primary};
  text-decoration: underline;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 16px;
  line-height: 24px;
`

export const RoleInfoCard = ({
  role,
  collapsible = false,
  defaultExpanded = true,
  testId
}: RoleInfoCardProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const { t } = useTranslation(NAMESPACE.TEAM)
  const { t: tReporting } = useTranslation(NAMESPACE.REPORTING)
  const nominative = useSelector(
    (s: { global: { home: { preferredPeopleNominative?: string } } }) =>
      s.global.home?.preferredPeopleNominative ?? 'service user'
  )

  const copy = ROLE_PREVIEW_COPY[role]
  if (!copy) return null

  // Some roles supply their own `roleNameKey` in the copy table.
  const roleName = copy.roleNameKey
    ? t(copy.roleNameKey)
    : tReporting(reportingConstants[role as UserRole])
  const heading = t(reportsConstants.ROLE_PREVIEW_HEADING_PREFIX, {
    role: roleName
  })

  const toggle = () => setExpanded((v) => !v)

  return (
    <Card $expanded={expanded} $bare={collapsible} data-testid={testId}>
      <Heading
        $clickable={collapsible}
        $linkStyle={collapsible}
        onClick={collapsible ? toggle : undefined}
        onKeyDown={
          collapsible
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  toggle()
                }
              }
            : undefined
        }
        role={collapsible ? 'button' : undefined}
        tabIndex={collapsible ? 0 : undefined}
        aria-expanded={collapsible ? expanded : undefined}
      >
        <InfoCircleFilled style={{ color: '#1890ff' }} />
        <HeadingLabel $underline={collapsible}>{heading}</HeadingLabel>
        {collapsible && (
          <Chevron aria-hidden>
            {expanded ? <UpOutlined /> : <DownOutlined />}
          </Chevron>
        )}
      </Heading>
      {expanded && (
        <ExpandedBody $bordered={collapsible}>
          {copy.sections.map((section) => (
            <div key={section.heading}>
              <SectionTitle>
                {section.heading === 'careOffice'
                  ? t(reportsConstants.ROLE_PREVIEW_CARE_OFFICE)
                  : t(reportsConstants.ROLE_PREVIEW_CARER_APP)}
              </SectionTitle>
              <SectionItems>
                {section.items.map((item, idx) => (
                  <Item key={`${section.heading}-${idx}`}>
                    {item.variant === 'tick' ? (
                      <TickGlyph aria-hidden />
                    ) : (
                      <CrossGlyph aria-hidden />
                    )}
                    <span>
                      <Trans
                        i18nKey={item.copyKey}
                        ns={NAMESPACE.TEAM}
                        values={{ serviceUser: nominative }}
                        components={{
                          settingsIcon: (
                            <InlineSettingsIcon
                              customIcon={IconType.Settings}
                              size={16}
                            />
                          )
                        }}
                      />
                    </span>
                  </Item>
                ))}
              </SectionItems>
            </div>
          ))}
          {copy.extraNote && (
            <ExtraNote>
              <Trans
                i18nKey={copy.extraNote.copyKey}
                ns={NAMESPACE.TEAM}
                components={{
                  bold: <strong />,
                  // NB: the tag must NOT be named `link` — `<link>` is an HTML
                  // void element, so react-i18next's parser self-closes it and
                  // drops the inner text to plaintext (CM-3784).
                  helpLink: (
                    <KeyworkerLink
                      href={copy.extraNote.linkHref}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <ExternalLinkIcon />
                    </KeyworkerLink>
                  )
                }}
              />
            </ExtraNote>
          )}
          <OverviewLink
            href={OVERVIEW_LINK_URL ?? undefined}
            target={OVERVIEW_LINK_URL ? '_blank' : undefined}
            rel={OVERVIEW_LINK_URL ? 'noreferrer' : undefined}
            onClick={OVERVIEW_LINK_URL ? undefined : (e) => e.preventDefault()}
            aria-disabled={OVERVIEW_LINK_URL ? undefined : 'true'}
          >
            {t(reportsConstants.ROLE_PREVIEW_OVERVIEW_LINK)}
            <ExternalLinkIcon />
          </OverviewLink>
        </ExpandedBody>
      )}
    </Card>
  )
}
